import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { Company } from '@interfaces/company';
import { CompaniesApiService } from '@services/api/companies/companies-api.service';
import { UserStorageService } from '@services/local-storage/user-storage/user-storage.service';
import { Roles } from '@enums/Roles';
import { User } from '@interfaces/user';

const ALL_KEY = 'xr_company_filter';
const DOCUMENTS_KEY = 'xr_documents_company';

/**
 * Company chosen by the superadmin in the "Azienda" menu, the same on every page and kept across visits.
 * Documenti always works on one company (its folders would mix otherwise), so it keeps its own choice.
 * The other roles only ever see their own company: for them there is no choice and nothing is filtered.
 */
@Injectable({ providedIn: 'root' })
export class CompanyFilterService {
  private companiesApi = inject(CompaniesApiService);
  private userStorage = inject(UserStorageService);

  private readonly selectedSubject = new BehaviorSubject<number | null>(this.read(ALL_KEY));
  private readonly documentsSubject = new BehaviorSubject<number | null>(this.read(DOCUMENTS_KEY));
  private readonly companiesSubject = new BehaviorSubject<Company[]>([]);
  private readonly superadminSubject = new BehaviorSubject<boolean>(false);
  private loading: Promise<void> | null = null;
  private loadedFor: number | null | undefined = undefined;

  /** null = all companies */
  readonly selected$: Observable<number | null> = this.selectedSubject.asObservable();
  readonly documentsCompany$: Observable<number | null> = this.documentsSubject.asObservable();
  readonly companies$: Observable<Company[]> = this.companiesSubject.asObservable();
  readonly isSuperadmin$: Observable<boolean> = this.superadminSubject.asObservable();

  get selected(): number | null {
    return this.selectedSubject.value;
  }

  get documentsCompany(): number | null {
    return this.documentsSubject.value;
  }

  get isSuperadmin(): boolean {
    return this.superadminSubject.value;
  }

  /** Loads the role and, for the superadmin, the companies, once per logged user; safe to call from every page */
  async load(): Promise<void> {
    const user = await this.userStorage.getUserStorage().catch(() => null);
    const key = user?.id ?? null;
    if (!this.loading || this.loadedFor !== key) {
      this.loadedFor = key;
      this.loading = this.doLoad(user);
    }
    return this.loading;
  }

  select(companyId: number | null): void {
    this.write(ALL_KEY, companyId);
    this.selectedSubject.next(companyId);
  }

  selectDocumentsCompany(companyId: number): void {
    this.write(DOCUMENTS_KEY, companyId);
    this.documentsSubject.next(companyId);
  }

  /** True when the item belongs to the chosen company (always true without a choice or for other roles) */
  matches(companyId: number | null | undefined): boolean {
    const selected = this.selectedSubject.value;
    return !this.isSuperadmin || selected === null || companyId === selected;
  }

  private async doLoad(user: User | null): Promise<void> {
    const superadmin = user?.roles?.id === Roles.SUPER_ADMIN;
    this.superadminSubject.next(superadmin);
    if (!superadmin) {
      return;
    }
    try {
      const response = await firstValueFrom(this.companiesApi.get_company_list());
      const companies = (response?.data ?? []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it'));
      this.companiesSubject.next(companies);
      const exists = (id: number | null) => id !== null && companies.some(c => c.id === id);
      // A choice saved for a company that no longer exists is dropped
      if (this.selectedSubject.value !== null && !exists(this.selectedSubject.value)) {
        this.select(null);
      }
      if (!exists(this.documentsSubject.value)) {
        const own = user?.company?.id;
        const start = exists(own ?? null) ? own! : companies[0]?.id;
        if (start) {
          this.selectDocumentsCompany(start);
        }
      }
    } catch {
      this.companiesSubject.next([]);
    }
  }

  private read(key: string): number | null {
    try {
      const value = localStorage.getItem(key);
      return value ? Number(value) || null : null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: number | null): void {
    try {
      if (value === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, String(value));
      }
    } catch {
      // private window or blocked storage: the choice lasts until the page is reloaded
    }
  }
}
